import { Link } from "@inertiajs/react"
import { useMemo, useState } from "react"
import { X } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Badge, type BadgeTone } from "@/components/ui/badge"
import { UserLink } from "@/components/ui/user-link"
import { Select } from "@/components/ui/select"
import { Sparkline } from "@/components/ui/sparkline"
import { fmtRelativeTime } from "@/lib/format"
import type { FeedbackDrawerData, FeedbackReportRow } from "@/types/user_feedback"

// Right-rail drawer for a single feedback item. v1 is read-only — no status
// dropdown, no action buttons, no sentiment chip. Source links to the
// originating ticket conversation; topics + related convos render only when
// present so empty states stay quiet.

type Props = {
  data: FeedbackDrawerData | null
  onClose: () => void
}

type ReportSortKey = "newest" | "oldest" | "customer"

const REPORT_SORT_OPTIONS: { value: ReportSortKey; label: string }[] = [
  { value: "newest",   label: "Newest first" },
  { value: "oldest",   label: "Oldest first" },
  { value: "customer", label: "Customer A->Z" },
]

const TONE_BY_CATEGORY: Record<string, BadgeTone> = {
  bug:            "red",
  recommendation: "gold",
  unresolved:     "warning",
  general:        "neutral",
  rating:         "success",
  bad_behavior:   "error",
}

function toneFor(category: string): BadgeTone {
  return TONE_BY_CATEGORY[category] ?? "neutral"
}

function fmtAbsolute(iso: string | null): string {
  if (!iso) return "—"
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleString()
}

export function FeedbackDetailDrawer({ data, onClose }: Props) {
  const [reportSearch, setReportSearch] = useState("")
  const [reportSort, setReportSort] = useState<ReportSortKey>("newest")

  const filteredReports = useMemo(() => {
    if (!data) return []
    return sortReports(filterReports(data.reports, reportSearch), reportSort)
  }, [data, reportSearch, reportSort])

  if (!data) {
    return (
      <Card className="p-6 text-sm text-app-muted">
        Select an item on the left to see details.
      </Card>
    )
  }

  return (
    <Card className="space-y-5 p-5">
      <header className="flex items-start justify-between gap-3">
        <div className="space-y-2">
          <Badge tone={toneFor(data.category)}>{data.categoryLabel}</Badge>
          {data.feedbackKey && (
            <div className="font-mono text-[11px] text-app-muted">{data.feedbackKey}</div>
          )}
          <h2 className="text-lg font-semibold leading-snug">{data.title}</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close detail"
          className="rounded-control p-1 text-app-muted hover:bg-white/5 hover:text-app-text"
        >
          <X size={18} strokeWidth={2} />
        </button>
      </header>

      {data.description && (
        <section className="space-y-1">
          <div className="text-xs uppercase tracking-wide text-app-subtle">Description</div>
          <p className="text-sm leading-relaxed text-app-text">{data.description}</p>
        </section>
      )}

      {data.customerQuote && (
        <section className="space-y-1">
          <div className="text-xs uppercase tracking-wide text-app-subtle">Customer quote</div>
          <Card variant="inset" className="p-3">
            <p className="text-sm italic leading-relaxed text-app-text">
              &ldquo;{data.customerQuote}&rdquo;
            </p>
            <div className="mt-2 text-xs text-app-muted">
              —{" "}
              <UserLink
                id={data.appuserId}
                name={data.customerName}
                email={data.customerEmail}
                className="text-app-muted hover:text-brand-red"
              />
              {data.customerSubmittedAt && (
                <span> · {fmtRelativeTime(data.customerSubmittedAt)}</span>
              )}
            </div>
          </Card>
        </section>
      )}

      <section className="space-y-1">
        <div className="text-xs uppercase tracking-wide text-app-subtle">Source</div>
        <div className="text-sm text-app-text">{data.agentName ?? "—"}</div>
        {data.skillName && (
          <div className="text-xs text-app-muted">{data.skillName}</div>
        )}
        {data.convoId != null && (
          <Link
            href={`/portal/tickets/${data.convoId}`}
            className="inline-block text-xs text-brand-red hover:text-brand-redDeep"
          >
            Open conversation →
          </Link>
        )}
      </section>

      <section className="space-y-1">
        <div className="text-xs uppercase tracking-wide text-app-subtle">Last reported</div>
        <div className="text-sm text-app-text">{fmtRelativeTime(data.lastReportedAt)}</div>
        <div className="text-xs text-app-muted">{fmtAbsolute(data.lastReportedAt)}</div>
      </section>

      <section className="space-y-1">
        <div className="text-xs uppercase tracking-wide text-app-subtle">Reports</div>
        <div className="text-sm text-app-text">{data.reportCount}</div>
        {data.duplicateCount > 0 && (
          <div className="text-xs text-app-muted">
            {data.duplicateCount} matched duplicate{data.duplicateCount === 1 ? "" : "s"}
          </div>
        )}
        <div className="text-xs text-app-muted">
          {data.uniqueAppuserCount} unique user{data.uniqueAppuserCount === 1 ? "" : "s"}
        </div>
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs uppercase tracking-wide text-app-subtle">Daily mentions</div>
          <div className="text-xs text-app-muted">Last {data.dailyReports.length || 14} days</div>
        </div>
        <div className="rounded-control border border-border bg-white/[0.03] p-3">
          <Sparkline
            values={data.dailyReports.map((point) => point.count)}
            height={48}
            variant="bar"
            fillColor="#e0455b"
          />
        </div>
      </section>

      <section className="space-y-2">
        <div className="text-xs uppercase tracking-wide text-app-subtle">Topics</div>
        {data.topics.length === 0 ? (
          <div className="text-xs text-app-muted">No topics tagged</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {data.topics.map((t) => (
              <Badge key={t.key} tone="neutral">
                {t.name}
              </Badge>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-app-subtle">Report details</div>
            {data.reportsTruncated && (
              <div className="mt-0.5 text-[11px] text-app-muted">Showing latest 100 reports</div>
            )}
          </div>
          <div className="w-[150px]">
            <Select<ReportSortKey>
              value={REPORT_SORT_OPTIONS.find((o) => o.value === reportSort) ?? REPORT_SORT_OPTIONS[0]}
              options={REPORT_SORT_OPTIONS}
              onChange={(o) => o && setReportSort(o.value)}
              isSearchable={false}
            />
          </div>
        </div>

        <input
          type="search"
          value={reportSearch}
          onChange={(e) => setReportSearch(e.target.value)}
          placeholder="Filter reports..."
          className="w-full rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text placeholder:text-app-muted focus:border-brand-red focus:outline-none"
        />

        {filteredReports.length === 0 ? (
          <div className="rounded-control border border-border bg-white/[0.03] px-3 py-4 text-xs text-app-muted">
            No matching report rows.
          </div>
        ) : (
          <div className="overflow-hidden rounded-control border border-border">
            <div className="grid grid-cols-[minmax(0,1fr)_88px] border-b border-border bg-app-panel2/30 px-3 py-2 text-[10px] uppercase tracking-wide text-app-muted">
              <div>Report</div>
              <div className="text-right">When</div>
            </div>
            <div className="divide-y divide-border">
              {filteredReports.map((report) => (
                <ReportRow key={report.id} report={report} />
              ))}
            </div>
          </div>
        )}
      </section>
    </Card>
  )
}

function ReportRow({ report }: { report: FeedbackReportRow }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_88px] gap-3 px-3 py-2 text-xs">
      <div className="min-w-0">
        <div className="truncate text-app-text">
          <UserLink
            id={report.appuserId}
            name={report.customerName}
            email={report.customerEmail}
            className="text-app-text hover:text-brand-red"
          />
        </div>
        {report.description && (
          <div className="mt-0.5 line-clamp-2 text-app-muted">{report.description}</div>
        )}
        {report.convoId != null && (
          <Link
            href={`/portal/tickets/${report.convoId}`}
            className="mt-1 inline-block text-[11px] text-brand-red hover:text-brand-redDeep"
          >
            Open conversation →
          </Link>
        )}
      </div>
      <div className="text-right text-[11px] text-app-muted">
        {fmtRelativeTime(report.createdAt)}
      </div>
    </div>
  )
}

function filterReports(reports: FeedbackReportRow[], query: string): FeedbackReportRow[] {
  const q = query.trim().toLowerCase()
  if (!q) return reports
  return reports.filter((report) => {
    return (
      (report.customerName ?? "").toLowerCase().includes(q) ||
      (report.customerEmail ?? "").toLowerCase().includes(q) ||
      (report.description ?? "").toLowerCase().includes(q) ||
      report.keywords.some((keyword) => keyword.toLowerCase().includes(q))
    )
  })
}

function sortReports(reports: FeedbackReportRow[], sort: ReportSortKey): FeedbackReportRow[] {
  return [...reports].sort((a, b) => {
    if (sort === "customer") {
      return (a.customerName ?? a.customerEmail ?? "").localeCompare(b.customerName ?? b.customerEmail ?? "")
    }
    const av = Date.parse(a.createdAt ?? "")
    const bv = Date.parse(b.createdAt ?? "")
    return sort === "oldest" ? av - bv : bv - av
  })
}
